import React, { useState, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
  useMediaQuery,
  ListItemIcon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, Link as RouterLink, useParams } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import logoImage from '../assets/logo2.png';
import { useAuth } from '../context/AuthContext';

function getInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function useBreadcrumbs() {
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    const path = location.pathname;
    const crumbs = [{ label: 'Inicio', to: '/', icon: <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} /> }];

    if (path.startsWith('/category-tasks/')) {
      const segments = path.split('/').filter(Boolean);
      const listName = segments[2] ? decodeURIComponent(segments[2]) : 'Lista';
      const status = segments[3] ? decodeURIComponent(segments[3]) : null;
      crumbs.push({ label: listName, to: null });
      if (status) crumbs.push({ label: status, to: null });
    } else if (path.startsWith('/dashboard/')) {
      const status = params.status ? decodeURIComponent(params.status) : 'Dashboard';
      crumbs.push({ label: status, to: null });
    } else if (path === '/about') {
      crumbs.push({ label: 'Acerca de', to: null });
    } else if (path === '/change-password') {
      crumbs.push({ label: 'Cambiar contraseña', to: null });
    }

    return crumbs;
  }, [location.pathname, params]);
}

function Header() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user, exchangeRate, exchangeRateDate, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const crumbs = useBreadcrumbs();

  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNavigate = (to) => {
    handleMenuClose();
    navigate(to);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login', { replace: true });
  };

  const userName = user?.nombre || user?.name || user?.email || 'Usuario';
  const isReadOnly = user?.isReadOnly === true;
  const showBreadcrumbs = isAuthenticated && location.pathname !== '/login' && crumbs.length > 1;

  return (
    <AppBar position="static" color="primary">
      <Toolbar sx={{ gap: 2 }}>
        <Box
          component="img"
          sx={{ height: 36, cursor: 'pointer' }}
          alt="Kepler"
          src={logoImage}
          onClick={() => navigate('/')}
        />
        <Box
          sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', minWidth: 0 }}
          onClick={() => navigate('/')}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}
          >
            Kepler Approval Board
          </Typography>
          {!isSmall && showBreadcrumbs && (
            <Breadcrumbs
              aria-label="breadcrumb"
              separator="›"
              sx={{
                color: 'grey.400',
                fontSize: 12,
                '& .MuiBreadcrumbs-separator': { mx: 0.5 },
              }}
            >
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1;
                if (isLast || !c.to) {
                  return (
                    <Typography
                      key={i}
                      sx={{
                        color: isLast ? 'common.white' : 'grey.400',
                        fontSize: 12,
                        fontWeight: isLast ? 600 : 400,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {c.icon}
                      {c.label}
                    </Typography>
                  );
                }
                return (
                  <MuiLink
                    key={i}
                    component={RouterLink}
                    to={c.to}
                    underline="hover"
                    sx={{
                      color: 'grey.400',
                      fontSize: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      '&:hover': { color: 'common.white' },
                    }}
                  >
                    {c.icon}
                    {c.label}
                  </MuiLink>
                );
              })}
            </Breadcrumbs>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {isAuthenticated && exchangeRate && !isSmall && (
          <Tooltip
            title={exchangeRateDate ? `Vigente al ${exchangeRateDate}` : 'Tipo de cambio'}
            arrow
          >
            <Chip
              icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />}
              label={`TC ${Number(exchangeRate).toFixed(4)}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'common.white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'secondary.main' },
              }}
            />
          </Tooltip>
        )}

        {isAuthenticated && isReadOnly && (
          <Tooltip title="Sesión con permisos de solo lectura" arrow>
            <Chip
              icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
              label="Solo lectura"
              size="small"
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}

        {!isSmall && (
          <Button
            color="inherit"
            startIcon={<InfoIcon />}
            onClick={() => navigate('/about')}
            sx={{ color: 'grey.300', '&:hover': { color: 'common.white' } }}
          >
            Acerca de
          </Button>
        )}

        {isAuthenticated ? (
          <>
            <Tooltip title={userName} arrow>
              <IconButton
                onClick={handleAvatarClick}
                size="small"
                sx={{
                  ml: 1,
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  p: 0.25,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'secondary.main',
                    color: 'common.white',
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {getInitials(userName)}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: { minWidth: 220, mt: 0.5, border: '1px solid', borderColor: 'divider' },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {userName}
                </Typography>
                {user?.email && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {user.email}
                  </Typography>
                )}
                {isReadOnly && (
                  <Box sx={{ mt: 0.75 }}>
                    <Chip
                      label="Solo lectura"
                      size="small"
                      color="warning"
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  </Box>
                )}
              </Box>
              <Divider />
              {isSmall && (
                <MenuItem onClick={() => handleNavigate('/about')}>
                  <ListItemIcon>
                    <InfoIcon fontSize="small" />
                  </ListItemIcon>
                  Acerca de
                </MenuItem>
              )}
              <MenuItem onClick={() => handleNavigate('/change-password')}>
                <ListItemIcon>
                  <LockResetIcon fontSize="small" />
                </ListItemIcon>
                Cambiar contraseña
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'secondary.dark' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: 'secondary.dark' }} />
                </ListItemIcon>
                Cerrar sesión
              </MenuItem>
            </Menu>
          </>
        ) : (
          isSmall && (
            <IconButton color="inherit" onClick={() => navigate('/about')} size="small">
              <InfoIcon />
            </IconButton>
          )
        )}
      </Toolbar>

      {isSmall && showBreadcrumbs && (
        <Box
          sx={{
            bgcolor: 'grey.900',
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderTopColor: 'grey.800',
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{
              color: 'grey.400',
              fontSize: 12,
              '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'grey.500' },
            }}
          >
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              if (isLast || !c.to) {
                return (
                  <Typography
                    key={i}
                    sx={{
                      color: isLast ? 'common.white' : 'grey.400',
                      fontSize: 12,
                      fontWeight: isLast ? 600 : 400,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {c.icon}
                    {c.label}
                  </Typography>
                );
              }
              return (
                <MuiLink
                  key={i}
                  component={RouterLink}
                  to={c.to}
                  underline="hover"
                  sx={{
                    color: 'grey.400',
                    fontSize: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {c.icon}
                  {c.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}
    </AppBar>
  );
}

export default Header;
